import { adminViewService } from '../services/maintenanceService';
import Button from './ui/Button';
import { Heart, ExternalLink, MessageCircle, Settings, RefreshCw, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function MaintenancePage({ message, feedbackUrl, isAdmin, onRefresh }) {
  const { signOut } = useAuth();

  const handleFeedbackClick = () => {
    window.open(feedbackUrl, '_blank');
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleSwitchToAdmin = () => {
    adminViewService.setViewMode('admin');
    window.location.reload();
  };

  const handleSwitchToLive = () => {
    adminViewService.setViewMode('live');
    window.location.reload();
  };

  return (
    <div className="min-h-screen">
      {/* Skip to main content link for keyboard users */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-white px-4 py-2 rounded-lg z-50"
      >
        Skip to main content
      </a>

      {/* On-Brand Navbar for Regular Users */}
      {!isAdmin && (
        <nav className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo - Same as main navbar */}
              <div className="flex items-center">
                <h1 className="text-2xl font-bold bg-gradient-hero bg-clip-text text-transparent" style={{ color: '#1f2937' }}>
                  CertifiAble
                </h1>
              </div>

              {/* Only show logout for maintenance mode */}
              <div className="flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  aria-label="Log out of CertifiAble"
                >
                  <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* Admin Controls Bar (only visible to admins) */}
      {isAdmin && (
        <>
          {/* On-Brand Navbar for Admins */}
          <nav className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                {/* Logo - Same as main navbar */}
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold bg-gradient-hero bg-clip-text text-transparent" style={{ color: '#1f2937' }}>
                    CertifiAble
                  </h1>
                </div>

                {/* Admin controls in navbar style */}
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={onRefresh}
                    aria-label="Refresh maintenance status"
                    className="min-h-[44px] min-w-[44px]"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
                    Refresh
                  </Button>
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={handleSwitchToLive}
                    aria-label="Switch to live dashboard view"
                    className="min-h-[44px]"
                  >
                    View Live Dashboard
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleSwitchToAdmin}
                    aria-label="Switch to admin dashboard"
                    className="min-h-[44px]"
                  >
                    Switch to Admin Dashboard
                  </Button>
                  <div className="h-6 w-px bg-gray-300 mx-2" />
                  <Button
                    variant="outline"
                    size="md"
                    onClick={handleLogout}
                    aria-label="Log out of CertifiAble"
                    className="min-h-[44px]"
                  >
                    <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
                    Sign Out
                  </Button>
                </div>
              </div>
            </div>
          </nav>
          
          {/* Admin Status Banner */}
          <div 
            className="bg-yellow-100 border-b border-yellow-300 px-4 py-2" 
            role="banner"
            aria-label="Admin status"
          >
            <div className="max-w-7xl mx-auto flex items-center justify-center">
              <div className="flex items-center gap-2 text-yellow-800">
                <Settings className="w-4 h-4" aria-hidden="true" />
                <span className="text-sm font-medium">Admin View: User Mode (Maintenance Active)</span>
                <span className="text-xs text-yellow-600 ml-4">
                  💡 Tip: Go to <code className="bg-yellow-200 px-1 rounded">/admin-override</code> or add <code className="bg-yellow-200 px-1 rounded">?admin=true</code> to any URL to return to admin mode
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-secondary py-24 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-8" role="img" aria-label="Maintenance tools">🛠️</div>
          
          <h1 className="text-7xl font-bold text-white mb-6 drop-shadow-lg">
            CertifiAble
          </h1>
          
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 mb-12 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-4">
              Temporarily Under Maintenance
            </h2>
            <p className="text-xl text-white drop-shadow-lg leading-relaxed">
              {message}
            </p>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-20 bg-white" id="main-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-8">
            <Heart className="w-24 h-24 text-red-500 mx-auto" role="img" aria-label="Heart symbol" />
          </div>
          
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Thank You for Your Patience
          </h2>
          
          <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              We're working hard to improve CertifiAble and make it even better for the ASL interpreter community. 
              Your continued support means everything to us.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              While we're making improvements, we'd love to hear from you. Your feedback helps us build 
              the best possible CEU tracking experience.
            </p>
          </div>

          {feedbackUrl && (
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                <MessageCircle className="w-8 h-8 inline-block mr-3 text-primary" aria-hidden="true" />
                Share Your Thoughts
              </h3>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Help us make CertifiAble better by sharing your feedback, suggestions, or any issues you've encountered.
              </p>
              
              <Button 
                variant="primary" 
                size="lg" 
                onClick={handleFeedbackClick}
                className="inline-flex items-center gap-3 min-h-[44px] focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="Share your feedback - opens in new window"
              >
                <ExternalLink className="w-5 h-5" aria-hidden="true" />
                Share Your Feedback
              </Button>
            </div>
          )}

          <div className="text-center">
            <p className="text-lg text-gray-600 leading-relaxed">
              Built by an interpreter, for interpreters. We'll be back soon!
            </p>
            <p className="text-primary font-semibold text-xl mt-4">
              — Danny Frank, Frankly Interpreting LLC
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}